"use client";

import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";

interface ScrollRevealProps {
	children: ReactNode;
	delay?: number;
	rootMargin?: string;
}

function subscribeReducedMotion(cb: () => void) {
	const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
	mq.addEventListener("change", cb);
	return () => mq.removeEventListener("change", cb);
}

function getReducedMotion() {
	return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function getReducedMotionServer() {
	return false;
}

export function ScrollReveal({ children, delay = 0, rootMargin = "0px 0px -10% 0px" }: ScrollRevealProps) {
	const ref = useRef<HTMLDivElement | null>(null);
	const [visible, setVisible] = useState(false);
	const reducedMotion = useSyncExternalStore(
		subscribeReducedMotion,
		getReducedMotion,
		getReducedMotionServer,
	);
	const isVisible = reducedMotion || visible;

	useEffect(() => {
		if (reducedMotion) return;
		const el = ref.current;
		if (!el) return;

		const obs = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						setVisible(true);
						obs.disconnect();
						break;
					}
				}
			},
			{ rootMargin, threshold: 0.05 },
		);
		obs.observe(el);
		return () => obs.disconnect();
	}, [rootMargin, reducedMotion]);

	return (
		<div
			ref={ref}
			style={{ transitionDelay: isVisible ? `${delay}ms` : "0ms" }}
			className={`transition-all duration-1100 ease-out-expo motion-reduce:transition-none ${
				isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
			}`}
		>
			{children}
		</div>
	);
}
